import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { DocumentData, limit, orderBy, QueryConstraint, QuerySnapshot, Unsubscribe, where } from 'firebase/firestore';

import { RegionHandles } from 'src/app/services/firebase/firebase.service';
import { MessagingService } from 'src/app/services/messaging/messaging.service';
import { MuteService } from 'src/app/services/mute/mute.service';
import { OrgSwitcherService } from 'src/app/services/org-switcher/org-switcher.service';
import { OrgTabsService } from 'src/app/services/org-tabs/org-tabs.service';
import { Region } from 'src/app/services/region/region-config';
import { RegionService } from 'src/app/services/region/region.service';
import { UserService } from 'src/app/services/user/user.service';
import { managerRosterKeys, ProjectManagerFields } from 'src/app/services/teams/roster-keys';
import { BackgroundConversation, nextCountDecision, orgTotal, railShowsInBackground, RailVisibility, tabCount } from './org-unread.util';

/** Same windows as MessagingListenerService's WATCHED queries — identical queries share one Firestore target. */
const AI_MAX = 200;
const CHANNEL_MAX = 50;

/** A project doc as the background rail needs it. */
type ProjectDoc = ProjectManagerFields & { conversationId?: string };

/**
 * The unread count on each open org tab (Vincent, 2026-10-22: "le # de requête
 * en attente par Co"). The org you are in shows NO count — you are looking at
 * its rail (Vincent: "when a tab is selected, I shouldn't see the counter").
 * The other open orgs are counted here, quietly (no notifications or sounds),
 * over exactly the rows their rail would hold (railShowsInBackground — project
 * managers hidden, project spaces shown): agent DMs and spaces come from the
 * same user-wide queries the listener already runs (they are not org-filtered
 * server-side), and each background org's projects from two small
 * membership-scoped listeners (the managers and project spaces). A
 * conversation is re-read only when its last message changes, team traffic
 * never counts, and nothing is listened to unless another org is open.
 *
 * Data residency (prod 2026-10-31): every background org is read through its
 * OWN region's project — handles that never follow the active tab — and the
 * user-wide queries run once per region that has an open background org. The
 * active region's handle refused another region's teams (permission-denied)
 * and its conversations simply aren't there, so a US tab's badge never counted
 * while an EU org was open.
 */
@Injectable({ providedIn: 'root' })
export class OrgUnreadService {
  private readonly regions = inject(RegionService);
  private readonly messaging = inject(MessagingService);
  private readonly mute = inject(MuteService);
  private readonly switcher = inject(OrgSwitcherService);
  private readonly orgTabs = inject(OrgTabsService);
  private readonly user = inject(UserService);

  private readonly backgroundTotals = signal<ReadonlyMap<string, number>>(new Map());

  /** Open orgs other than the one you are in. */
  private readonly backgroundIds = computed(() => {
    const current = this.switcher.currentOrgId();
    return this.orgTabs.openOrgIds().filter((id) => id !== current);
  });

  /** Per region with an open background org: its user-wide ai / channel queries and their rows. */
  private readonly shared = new Map<Region, { unsubs: Unsubscribe[]; ai: BackgroundConversation[]; channel: BackgroundConversation[] }>();
  /** Per background org: its region's handles (fixed — they don't follow the active tab). */
  private readonly orgHandles = new Map<string, RegionHandles>();
  private readonly projectUnsubs = new Map<string, Unsubscribe[]>();
  /** Per background org: its projects (member-of and created-by listeners, merged by id). */
  private readonly projects = new Map<string, { member: Map<string, ProjectDoc>; created: Map<string, ProjectDoc> }>();
  /** Per background org: each counted conversation's last message id and count. */
  private readonly state = new Map<string, Map<string, { lastMessageId?: string; count: number }>>();
  /** conversationId → the person's read marker (null = never opened). Cleared for an org when you leave it. */
  private readonly readMarkers = new Map<string, string | null>();
  private watched = new Set<string>();
  private queue: Promise<void> = Promise.resolve();

  constructor() {
    effect(() => this.sync(this.backgroundIds()));
    inject(DestroyRef).onDestroy(() => this.stopAll());
  }

  /** The circle for an org's tab: undefined (nothing — always for the org you are in), 1–9 or '9+'. */
  public countFor(orgId: string): number | '9+' | undefined {
    if (orgId === this.switcher.currentOrgId()) return undefined;
    return tabCount(this.backgroundTotals().get(orgId) ?? 0);
  }

  private sync(ids: string[]): void {
    const uid = this.user.user?.uid;
    if (!uid || !ids.length) {
      this.stopAll();
      return;
    }
    const next = new Set(ids);
    for (const orgId of this.watched) {
      if (next.has(orgId)) continue;
      // Closed, or now the org you are in: its own rail takes over.
      this.projectUnsubs.get(orgId)?.forEach((u) => u());
      this.projectUnsubs.delete(orgId);
      this.projects.delete(orgId);
      this.state.delete(orgId);
      this.orgHandles.delete(orgId);
    }
    const added = [...next].filter((orgId) => !this.watched.has(orgId));
    this.watched = next;
    this.stopUnusedRegions();
    for (const orgId of added) {
      // Just left this org: what you read there must be re-read, not cached.
      this.forgetReadMarkers(orgId);
      void this.startOrg(orgId, uid);
    }
    this.publish();
  }

  /** Listen to a background org through its own region (connected without re-binding the active one). */
  private async startOrg(orgId: string, uid: string): Promise<void> {
    let handles: RegionHandles;
    try {
      handles = await this.regions.handlesFor(orgId, { followBinding: false });
    } catch (error) {
      console.warn(`[org-unread] no count for ${orgId}: its region is unavailable`, error);
      return;
    }
    if (!this.watched.has(orgId) || this.projectUnsubs.has(orgId)) return;
    this.orgHandles.set(orgId, handles);
    // Same two membership-scoped queries as TeamsService (no composite index).
    const entry = { member: new Map<string, ProjectDoc>(), created: new Map<string, ProjectDoc>() };
    this.projects.set(orgId, entry);
    this.projectUnsubs.set(orgId, [
      this.listenProjects(handles, orgId, where('userIds', 'array-contains', uid), entry.member),
      this.listenProjects(handles, orgId, where('createdBy', '==', uid), entry.created),
    ]);
    if (!this.shared.has(handles.region)) {
      const rows = { unsubs: [] as Unsubscribe[], ai: [] as BackgroundConversation[], channel: [] as BackgroundConversation[] };
      this.shared.set(handles.region, rows);
      rows.unsubs = [
        this.listen(handles, sharedQuery(uid, 'ai', AI_MAX), (docs) => { rows.ai = docs; }),
        this.listen(handles, sharedQuery(uid, 'channel', CHANNEL_MAX), (docs) => { rows.channel = docs; }),
      ];
    }
  }

  /** A region's user-wide queries stop once no open background org lives there. */
  private stopUnusedRegions(): void {
    const used = new Set([...this.watched].map((orgId) => this.orgHandles.get(orgId)?.region).filter((r): r is Region => !!r));
    for (const [region, rows] of this.shared) {
      if (used.has(region)) continue;
      rows.unsubs.forEach((u) => u());
      this.shared.delete(region);
    }
  }

  private listen(handles: RegionHandles, constraints: QueryConstraint[], apply: (docs: BackgroundConversation[]) => void): Unsubscribe {
    return handles.firestore.collectionListen('conversations', constraints, (snapshot) => {
      apply(toConversations(snapshot));
      this.schedule();
    });
  }

  private listenProjects(handles: RegionHandles, orgId: string, constraint: QueryConstraint, into: Map<string, ProjectDoc>): Unsubscribe {
    return handles.firestore.collectionListen(`organizations/${orgId}/teams`, [constraint], (snapshot) => {
      into.clear();
      for (const doc of snapshot.docs) into.set(doc.id, doc.data() as ProjectDoc);
      this.schedule();
    });
  }

  /** Every row the user-wide queries hold, across regions. */
  private allConversations(): BackgroundConversation[] {
    return [...this.shared.values()].flatMap((rows) => [...rows.ai, ...rows.channel]);
  }

  /** The rail an org would show, from its projects. */
  private railFor(orgId: string): RailVisibility {
    const entry = this.projects.get(orgId);
    const all = entry ? [...new Map([...entry.member, ...entry.created]).values()] : [];
    return {
      managers: managerRosterKeys(all),
      projectSpaceIds: new Set(all.map((p) => p.conversationId).filter((id): id is string => !!id)),
    };
  }

  private schedule(): void {
    this.queue = this.queue
      .then(() => this.recompute())
      .catch((error) => console.error('[org-unread] recompute failed', error));
  }

  private async recompute(): Promise<void> {
    const uid = this.user.user?.uid;
    if (!uid) return;
    const muted = this.mute.mutedIds$.getValue();
    for (const orgId of this.watched) {
      const handles = this.orgHandles.get(orgId);
      if (!handles) continue;
      const rows = this.shared.get(handles.region);
      const rail = this.railFor(orgId);
      const byId = new Map<string, BackgroundConversation>();
      for (const c of [...(rows?.ai ?? []), ...(rows?.channel ?? [])]) {
        if (c.organizationId === orgId && !muted.has(c.id) && railShowsInBackground(c, rail)) byId.set(c.id, c);
      }
      const db = handles.firestore;
      await this.fetchMissingReadMarkers([...byId.keys()], uid, db);
      const previous = this.state.get(orgId) ?? new Map();
      const next = new Map<string, { lastMessageId?: string; count: number }>();
      await Promise.all([...byId.values()].map(async (c) => {
        const marker = this.readMarkers.get(c.id) ?? null;
        const decision = nextCountDecision(c, previous.get(c.id), uid);
        const count = decision.kind === 'keep' ? decision.count
          : decision.kind === 'zero' ? 0
          : marker ? await this.messaging.getMessagesCountAfter(c.id, marker, db) : await this.messaging.countUnreadInLatest(c.id, db);
        next.set(c.id, { lastMessageId: c.lastMessage?.id, count });
      }));
      if (this.watched.has(orgId)) this.state.set(orgId, next);
    }
    this.publish();
  }

  private async fetchMissingReadMarkers(ids: string[], uid: string, db: RegionHandles['firestore']): Promise<void> {
    const missing = ids.filter((id) => !this.readMarkers.has(id));
    await Promise.all(missing.map(async (id) => {
      const marker = await this.messaging.fetchLastReadMessageId(id, uid, db).catch(() => undefined);
      this.readMarkers.set(id, marker || null);
    }));
  }

  private forgetReadMarkers(orgId: string): void {
    for (const c of this.allConversations()) {
      if (c.organizationId === orgId) this.readMarkers.delete(c.id);
    }
  }

  private publish(): void {
    const totals = new Map<string, number>();
    for (const orgId of this.watched) {
      totals.set(orgId, orgTotal([...(this.state.get(orgId)?.values() ?? [])].map((s) => s.count)));
    }
    this.backgroundTotals.set(totals);
  }

  private stopAll(): void {
    this.shared.forEach((rows) => rows.unsubs.forEach((u) => u()));
    this.shared.clear();
    this.orgHandles.clear();
    this.projectUnsubs.forEach((unsubs) => unsubs.forEach((u) => u()));
    this.projectUnsubs.clear();
    this.projects.clear();
    this.state.clear();
    this.readMarkers.clear();
    this.watched = new Set();
    this.backgroundTotals.set(new Map());
  }
}

function sharedQuery(uid: string, category: 'ai' | 'channel', max: number): QueryConstraint[] {
  return [
    where('userIds', 'array-contains', uid),
    where('category', '==', category),
    where('closed', '==', false),
    orderBy('updatedOn', 'desc'),
    limit(max),
  ];
}

function toConversations(snapshot: QuerySnapshot<DocumentData>): BackgroundConversation[] {
  return snapshot.docs.map((doc) => {
    const d = doc.data();
    const last = d.lastMessage;
    return {
      id: doc.id,
      category: d.category,
      organizationId: d.organizationId ?? null,
      closed: d.closed === true,
      agentId: d.agentId ?? null,
      sharedAgentId: d.sharedAgentId ?? null,
      lastMessage: last ? { id: last.id, sender: last.sender, teamTraffic: last.teamTraffic === true } : null,
    };
  });
}
